import FeatureGates from '@atlaskit/feature-gate-js-client';
import { SOURCE_COMPONENT, SOURCE_CONTEXT, TwcXflowModal } from '@atlassian/twc-xflow-modal';
import { type LogoProps } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import { usePostOfficeContext } from '@atlassian/post-office-context';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { type Options, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { css, styled } from '@compiled/react';
import { RecommendationControls } from '@post-office/components--recommendation-controls';
import { useIsWacContext } from '@post-office/growth-common';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { CROSS_FLOW_MODAL_WIDTH, CROSS_FLOW_MODAL_HEIGHT } from '@atlassian/cross-flow-next-ui';
import { FullScreenModalTrigger } from '@atlassian/entry-points/full-screen-modal-trigger';
import { crossFlowModalEntryPoint } from '@post-office/components--cross-flow-modal';
import { getProjectCreationContextParams } from '@post-office/components--cross-flow-modal/project-context';
import React, { type ComponentType, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { type AppRecommendation } from './types';
import { type JSX } from 'react/jsx-runtime';

import { getJourney } from './utils/get-journey';

const CROSS_FLOW_SOURCE_COMPONENT = 'atlassian-switcher';

const CROSS_FLOW_SOURCE_CONTEXT = 'try';

const JSMGROWTH_462_EXPERIMENT_KEY = 'jsmgrowth_462_improve_switcher_signups_v2';
const LOOM_HAMS_ID = 'loom';

/*--body--*/

type BodyProps = {
	description: React.ReactNode;
	name: string;
	lozenge?: React.ReactNode;
};

type BodyDescriptionProps = {
	isInWacContext: boolean;
};

const bodyPositioningStyles = css({
	overflow: 'hidden',
	fontWeight: 400,
	display: 'flex',
	flexDirection: 'column',
});

const BodyTitle = styled.span<BodyDescriptionProps>(() => ({
	fontSize: '14px',
	fontWeight: 400,
	marginTop: token('space.050', '4px'),
	minWidth: 0,
	lineHeight: '16px',
	color: token('color.text', '#172B4D'),
	display: 'flex',
	alignItems: 'flex-start',
	gap: token('space.050', '4px'),
}));

const BodyTitleName = styled.span(() => ({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: 0,
	maxWidth: 'fit-content',
	flex: '1 1 auto',
}));

const BodyTitleLozenge = styled.span(() => ({
	flexShrink: 0,
}));

const BodyDescription = styled.span<BodyDescriptionProps>((props) => ({
	marginTop: props.isInWacContext ? token('space.025', '2px') : token('space.050', '4px'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	color: token('color.text.subtlest', '#6B778C'),
	lineHeight: token('space.200', '16px'),
	fontSize: props.isInWacContext ? '12px' : '11px',
	font: token('font.body.small'),
}));

export const Body = ({ name, description, lozenge }: BodyProps): JSX.Element => {
	const isInWacContext = useIsWacContext();
	return (
		<span css={bodyPositioningStyles}>
			<BodyTitle isInWacContext={isInWacContext}>
				<BodyTitleName>{name}</BodyTitleName>
				{lozenge && <BodyTitleLozenge>{lozenge}</BodyTitleLozenge>}
			</BodyTitle>
			<BodyDescription isInWacContext={isInWacContext}>{description}</BodyDescription>
		</span>
	);
};

/*--logo--*/

export type RecommendationLogoProps = {
	Logo: string | ComponentType<LogoProps>;
};

type LogoContainerProps = {
	hasShadow: boolean;
};

const logoPositioningStyles = css({
	marginRight: token('space.100', '8px'),
	display: 'flex',
	alignItems: 'center',
});

const LogoContainer = styled.span<LogoContainerProps>(
	{
		width: token('space.400', '32px'),
		height: token('space.400', '32px'),
		boxSizing: 'border-box',
		backgroundColor: token('color.background.neutral', '#FFFFFF'),
		borderRadius: token('radius.large', '8px'),
		display: 'flex',
		overflow: 'hidden',
		alignItems: 'center',
		justifyContent: 'center',
	},
	(props) => ({
		boxShadow: props.hasShadow
			? token(
					'elevation.shadow.raised',
					`0 1px 1px ${'rgba(9, 30, 66, 0.25)'}, 0 0 1px 1px ${'rgba(9, 30, 66, 0.13)'}`,
				)
			: undefined,
	}),
);

const logoImageStyles = css({
	width: token('space.400', '32px'),
	height: token('space.400', '32px'),
});

const RecommendationLogo = ({ Logo }: RecommendationLogoProps) => {
	return (
		<span css={logoPositioningStyles}>
			<LogoContainer hasShadow={false}>
				{typeof Logo === 'string' ? (
					<img css={logoImageStyles} src={Logo} alt="" />
				) : (
					<Logo appearance="brand" size="medium" />
				)}
			</LogoContainer>
		</span>
	);
};

/*--recommendation--*/

const recommendationPositioningStyles = css({
	// Required for placements that don't have a css reset for buttons, without this we default to the browser's default stylesheet font family
	'& button': {
		fontFamily: 'inherit',
	},
	display: 'flex',
	width: '100%',
	justifyItems: 'stretch',
	justifyContent: 'flex-end',
	gap: token('space.025', '3px'),
	height: token('space.600', '48px'),
	alignItems: 'center',
	margin: token('space.0', '0px'),
});

const recommendationContainerStyles = css({
	borderRadius: token('radius.small', '3px'),
	background: 'none',
	border: 'none',
	textAlign: 'left',
	display: 'flex',
	flexGrow: 1,
	backgroundColor: 'transparent',
	padding: `${token('space.050', '4px')} ${token('space.100', '8px')}`,
	lineHeight: '18px',
	alignItems: 'stretch',
	minWidth: 0,
	color: token('color.text', '#172B4D'),
	outlineColor: token('color.border.focused', '#4C9AFF'),
	':hover': {
		cursor: 'pointer',
		backgroundColor: token('color.background.neutral.subtle', '#EBECF0'),
		textDecoration: 'none',
		color: token('color.text', '#172B4D'),
	},
	height: token('space.600', '48px'),
});

const recommendationContainerWithHoverStyles = css({
	// backgroundColor: token('color.background.neutral.subtle.hovered', N20A),
	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered', 'rgba(9, 30, 66, 0.04)'),
		borderRadius: token('radius.small', '4px'),
	},
	// override a transition that has a short, but noticeable delay
	transition: 'background 0s ease-out 0s',
});

export type Props = {
	app: AppRecommendation;
	onDismissed: (messageInstanceId: string) => void;
	extraAnalyticsAttributes?: Record<string, string | number | boolean>;
	/** An optional callback function. If provided, it will override the default cross flow behavior */
	onClick?: () => void;
	lozenge?: React.ReactNode;
};

export const FirstPartyRecommendation = ({
	app,
	onDismissed,
	onClick,
	extraAnalyticsAttributes,
	lozenge,
}: Props): JSX.Element => {
	// This is to handle a specific design requirement to hightlight the dropdown on hover
	// Couldn't work out how to do this in css, so its js :(
	const crossFlow = useCrossFlow();
	const { messageClicked } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { fireInteractedUIEvent: fireCrossFlowInteractedUIEvent } =
		useFireCrossFlowAnalyticsEvents();
	const isInWacContext = useIsWacContext();
	const { isSiteAdmin, tenantId } = usePostOfficeContext();
	const { formatMessage, locale } = useIntl();
	const [isLoomInterceptModalOpen, setIsLoomInterceptModalOpen] = useState(false);

	const isJsmOrJpdRecommendation =
		app.hamsIdentifier === Targets.JIRA_SERVICE_DESK ||
		app.hamsIdentifier === Targets.JIRA_PRODUCT_DISCOVERY;

	const isSwitcherSignupModalVariation =
		isSiteAdmin &&
		isJsmOrJpdRecommendation &&
		FeatureGates.getExperimentValue<boolean>(JSMGROWTH_462_EXPERIMENT_KEY, 'isEnabled', false, {
			fireExperimentExposure: false,
		});

	const crossFlowModalContextParams = useMemo(
		() =>
			getProjectCreationContextParams({
				targetProduct: app.hamsIdentifier,
				templateKey: app.templateKey,
				formatMessage,
			}),
		[app.hamsIdentifier, app.templateKey, formatMessage],
	);

	const handleInstallApp = useCallback(async () => {
		await messageClicked();
		await messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'recommendation',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'acknowledge',
			},
			additionalMetadataFromFrontend: {},
		});
		fireCrossFlowInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'listItem',
				actionSubjectId: 'product',
			},
			{
				source: 'atlassianSwitcherDiscoverProductsSection',
				targetProductId: app.ccpProductId,
			},
			{
				...extraAnalyticsAttributes,
				isSiteAdmin,
			},
		);

		if (onClick) {
			onClick();
			return;
		}

		if (isSiteAdmin && isJsmOrJpdRecommendation) {
			FeatureGates.manuallyLogExperimentExposure(JSMGROWTH_462_EXPERIMENT_KEY);
		}

		if (isSwitcherSignupModalVariation) {
			return;
		}
		const crossFlowProps: Options = {
			journey: getJourney(app),
			targetProduct: app.hamsIdentifier,
			sourceComponent: CROSS_FLOW_SOURCE_COMPONENT,
			sourceContext: CROSS_FLOW_SOURCE_CONTEXT,
			...(app.extensions ? { extensions: app.extensions } : {}),
			...(extraAnalyticsAttributes ? { extraAnalyticsAttributes } : {}),
		};

		if (isInWacContext) {
			crossFlowProps.extraAnalyticsAttributes = {
				...(crossFlowProps.extraAnalyticsAttributes ?? {}),
				flagKey: 'wac_switcher_recommendations',
				cohort: 'experiment',
			};
		}

		if (crossFlow.isEnabled) {
			const { sourceComponent } = crossFlowProps;
			const targetProduct = app.hamsIdentifier;
			const isLoomInterceptEnabled =
				sourceComponent === 'atlassian-switcher' && targetProduct === LOOM_HAMS_ID && !!tenantId;

			if (isLoomInterceptEnabled) {
				setIsLoomInterceptModalOpen(true);
			} else {
				void crossFlow.api.open({ ...crossFlowProps });
			}
		}
	}, [
		app,
		crossFlow,
		fireCrossFlowInteractedUIEvent,
		isInWacContext,
		messageClicked,
		messageInstanceClicked,
		onClick,
		isSiteAdmin,
		extraAnalyticsAttributes,
		isSwitcherSignupModalVariation,
		isJsmOrJpdRecommendation,
		tenantId,
	]);

	// Button content shared between both cohorts
	const buttonContent = (
		<>
			<RecommendationLogo Logo={app.logo} />
			<Body name={app.appName} lozenge={app.appNameLozenge} description={app.description} />
		</>
	);

	return (
		<>
			<li
				css={[recommendationPositioningStyles, recommendationContainerWithHoverStyles]}
				data-testid="recommendation-list-item"
			>
				{isSwitcherSignupModalVariation ? (
					<FullScreenModalTrigger
						entryPoint={crossFlowModalEntryPoint}
						entryPointParams={{}}
						entryPointProps={{
							sourceComponent: CROSS_FLOW_SOURCE_COMPONENT,
							sourceContext: CROSS_FLOW_SOURCE_CONTEXT,
							targetProduct: app.hamsIdentifier,
							shouldRedirectOnSuccess: true,
							fakeBackground: 'jira',
							contextParams: crossFlowModalContextParams,
							width: CROSS_FLOW_MODAL_WIDTH,
							height: CROSS_FLOW_MODAL_HEIGHT,
						}}
						modalProps={{
							shouldCloseOnEscapePress: true,
						}}
					>
						{({ ref }) => (
							<button
								{...(ref && { ref: ref as React.Ref<HTMLButtonElement> })}
								onClick={handleInstallApp}
								css={[recommendationContainerStyles]}
							>
								{buttonContent}
							</button>
						)}
					</FullScreenModalTrigger>
				) : (
					<button onClick={handleInstallApp} css={[recommendationContainerStyles]}>
						{buttonContent}
					</button>
				)}
				{lozenge ? lozenge : null}
				<RecommendationControls
					app={app}
					onDismissed={onDismissed}
					additionalAnalyticsAttributes={extraAnalyticsAttributes}
					iconSize={'medium'}
				/>
				{isLoomInterceptModalOpen && tenantId && (
					<TwcXflowModal
						cloudId={tenantId}
						locale={locale}
						onClose={() => setIsLoomInterceptModalOpen(false)}
						sourceComponent={SOURCE_COMPONENT.ATLASSIAN_SWITCHER}
						sourceContext={SOURCE_CONTEXT.ATLASSIAN_SWITCHER_LOOM_INTERCEPT}
					/>
				)}
			</li>
		</>
	);
};
